<template>
  <div>
    <p>Click on a category to edit its ranges.</p>
    <b-alert :show="isEditingMaster" variant="danger">You are editing the master concentration scale.</b-alert>
    <b-table @row-clicked="onScaleRowClicked" :items="scales" :fields="scalesFields" v-if="scaleSet">
      <template v-slot:cell(range1Begin)="data">
        <span v-if="!data.item.isEditing">{{data.item.item.range1Begin}}</span>
        <b-form-input v-if="data.item.isEditing" v-model="data.item.item.range1Begin" type="number" />
      </template>
      <template v-slot:cell(range2Begin)="data">
        <span v-if="!data.item.isEditing">{{data.item.item.range2Begin}}</span>
        <b-form-input v-if="data.item.isEditing" v-model="data.item.item.range2Begin" type="number" />
      </template>
      <template v-slot:cell(range3Begin)="data">
        <span v-if="!data.item.isEditing">{{data.item.item.range3Begin}}</span>
        <b-form-input v-if="data.item.isEditing" v-model="data.item.item.range3Begin" type="number" />
      </template>
      <template v-slot:cell(range4Begin)="data">
        <span v-if="!data.item.isEditing">{{data.item.item.range4Begin}}</span>
        <b-form-input v-if="data.item.isEditing" v-model="data.item.item.range4Begin" type="number" />
      </template>
      <template v-slot:cell(isEditing)="data">
        <b-button type="button" variant="primary" v-if="data.item.isEditing" @click="onScaleSaved(data);">
            Save
        </b-button>
        <b-button type="button" v-if="data.item.isEditing" @click="data.item.isEditing = false;">
            Cancel
        </b-button>
      </template>
    </b-table>
    <b-row v-if="scaleSet && stationId && isEditable">
      <b-col v-if="isMaster">
        <p>This station uses the master concentration scale.</p>
        <b-button @click="switchToCustomScaleSet">Switch to Custom Scale</b-button>
      </b-col>
      <b-col v-if="!isMaster">
        <p>This station does not use the master concentration scale.</p>
        <b-button @click="switchToMasterScaleSet">Switch to Master Scale</b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import CrudOperationsMixin from '../Common/CrudOperationsMixin.js'

export default {
  name: "StationScales",
  mixins: [CrudOperationsMixin],
  props: [
    'stationId',
    'scaleSet',
    'isEditable',
    'noMasterWarnings'
  ],
  data() {
    return {
      isMaster: false,
      scaleSetId: null,
      scales: null,
      scalesFields: [
        {
          key: 'item.category',
          label: 'Category',
          formatter: x => {
            return this.formatCategory(x);
          }
        },
        {
          key: 'range1Begin',
          label: 'Low'
        },
        {
          key: 'range2Begin',
          label: 'Moderate'
        },
        {
          key: 'range3Begin',
          label: 'High'
        },
        {
          key: 'range4Begin',
          label: 'Very High'
        },
        {
          key: 'isEditing',
          label: ''
        }
      ],
    };
  },
  computed: {
    isEditingMaster: function () {
      return this.isMaster && this.scales && this.scales.some(s => s.isEditing);
    }
  },
  methods: {
    loadScales: function (scales) {
      this.scales = scales.map(s => {
        return {
          isEditing: false,
          item: s
        }
      });
      this.scales = this.scales.sort((a, b) => {
        if (a.item.category < b.item.category)
          return -1;
        else if (a.item.category > b.item.category)
          return 1;
        else
          return 0;
      });
    },
    formatCategory: function (x) {
      return x[0].toUpperCase() + x.substring(1).toLowerCase();
    },
    onScaleRowClicked: function (item, index, event) {
      if (item.isEditing || !this.isEditable)
        return;

      if (this.isMaster && !this.noMasterWarnings) {
        this.$bvModal.msgBoxConfirm('This station uses the master concentration scale. If you edit it, the changes will be applied to all stations that use the master concentration scale. Are you sure you want to edit it?', {
            title: 'Edit Master Concentration Scale',
            okVariant: 'danger',
            okTitle: 'Yes, Edit the Master Concentration Scale',
            cancelTitle: 'No, Do Not Edit the Master Concentration Scale'
          })
          .then(value => {
            if (value)
              this.onEditScaleRow(item, index);
          })
      } else {
        this.onEditScaleRow(item, index);
      }
    },
    onEditScaleRow: function (item, index) {
        item.isEditing = true;
    },
    onScaleSaved: function (data) {
      data.item.isEditing = false;
      data.item.item.scaleSetId = this.scaleSetId;

      this.query('/graphql/management', {
        query: `
          mutation ($id: ID!, $scale: ScaleUpdateInputOGT!) {
            updateScale(id: $id, scale: $scale) {
              id,
              concurrencyCheck
            }
          }`,
        variables: {
          'id': data.item.item.id,
          'scale': data.item.item
        }}, (updatedItem) => {
          data.item.item.concurrencyCheck = updatedItem.updateScale.concurrencyCheck
        }, null, 'Concentration Scale Updated', 'Error Updating Concentration Scale', 'The concentration scale for ' + this.formatCategory(data.item.item.category) + ' was updated successfully.');
    },
    switchToMasterScaleSet: function () {
      this.$bvModal.msgBoxConfirm('Are you sure you want to switch to the master concentration scale? The custom concentration scale for this station will be deleted.', {
        title: 'Switch to Master Concentration Scale',
        okVariant: 'danger',
        okTitle: 'Yes',
        cancelTitle: 'No',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      })
      .then(value => {
        if (!value)
          return;

        this.query('/graphql/management', {
          query: `
            mutation ($id: ID!) {
              switchStationToMasterScaleSet(id: $id) { 
                id,
                scaleSet {
                  id,
                  concurrencyCheck,
                  isMaster,
                  scales {
                    id,
                    category,
                    range1Begin,
                    range2Begin,
                    range3Begin,
                    range4Begin,
                    concurrencyCheck
                  }
                }
              }
            }`,
          variables: {
            'id': this.stationId,
          }}, (data) => {
            this.loadScales(data.switchStationToMasterScaleSet.scaleSet.scales);
            this.isMaster = data.switchStationToMasterScaleSet.scaleSet.isMaster;
            this.scaleSetId = data.switchStationToMasterScaleSet.scaleSet.id;
          }, null, 'Switch to Master Concentration Scale', 'Error Switching to Master Concentration Scale', 'The station was successfully switched to the master concentration scale.');
      });      
    },
    switchToCustomScaleSet: function () {
      this.$bvModal.msgBoxConfirm('Are you sure you want to switch to a custom concentration scale?', {
        title: 'Switch to Custom Concentration Scale',
        okVariant: 'danger',
        okTitle: 'Yes',
        cancelTitle: 'No',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      })
      .then(value => {
        if (!value)
          return;

        this.query('/graphql/management', {
          query: `
            mutation ($id: ID!) {
              switchStationToCustomScaleSet(id: $id) { 
                id,
                scaleSet {
                  id,
                  concurrencyCheck,
                  isMaster,
                  scales {
                    id,
                    category,
                    range1Begin,
                    range2Begin,
                    range3Begin,
                    range4Begin,
                    concurrencyCheck
                  }
                }
              }
            }`,
          variables: {
            'id': this.stationId,
          }}, (data) => {
            this.loadScales(data.switchStationToCustomScaleSet.scaleSet.scales);
            this.isMaster = data.switchStationToCustomScaleSet.scaleSet.isMaster;
            this.scaleSetId = data.switchStationToCustomScaleSet.scaleSet.id;
          }, null, 'Switch to Custom Concentration Scale', 'Error Switching to Custom Concentration Scale', 'The station was successfully switched to a custom concentration scale.');
      });      
    }    
  },
  mounted () {
    this.loadScales(this.scaleSet.scales);
    this.isMaster = this.scaleSet.isMaster;
    this.scaleSetId = this.scaleSet.id;
  }
};
</script>